import { useEffect } from "react";
import ROUTES from "@/share-routes.json";

const ORIGIN = process.env.REACT_APP_SITE_ORIGIN;
const NOT_FOUND = {
  slug: "home",
  title: "This room has not been lit yet — Prism Pixel™",
  description: "An unlit room. Return to the threshold.",
};

export function shareRouteFor(path) {
  const pathname = path.replace(/\/+$/, "") || "/";
  return (
    ROUTES.find((r) => r.path === pathname) ||
    ROUTES.find((r) => r.prefix && pathname.startsWith(`${r.path}/`)) ||
    ROUTES[0]
  );
}

function setTag(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => k !== "tag" && el.setAttribute(k, v));
}

const meta = (key, content, attr = "property") =>
  setTag(`meta[${attr}="${key}"]`, { [attr]: key, content });

export function setMakerMeta({ slug, name, title }) {
  const image = `${ORIGIN}/share/makers/${slug}.jpg`;
  const t = `${name} — The First Ten · Prism Pixel™`;
  const desc = `${name}, ${title}. One of the first ten to hold the material before the world had a name for it.`;
  document.title = t;
  meta("description", desc, "name");
  meta("og:title", t);
  meta("og:description", desc);
  meta("og:image", image);
  meta("twitter:title", t, "name");
  meta("twitter:description", desc, "name");
  meta("twitter:image", image, "name");
}

// Inverse Archive entries carry their own title, descriptor and share portrait.
export function setArchiveMeta({ number, title, summary, slug }) {
  const t = `Inverse Archive ${number} — ${title} · PrismPixel™`;
  const desc = summary.length > 158 ? `${summary.slice(0, 155).trimEnd()}…` : summary;
  const image = archiveShareImage(slug);
  document.title = t;
  meta("description", desc, "name");
  meta("og:title", t);
  meta("og:description", desc);
  meta("og:image", image);
  meta("twitter:title", t, "name");
  meta("twitter:description", desc, "name");
  meta("twitter:image", image, "name");
}

export const archiveShareImage = (slug) => (slug ? `${ORIGIN}/share/archive/${slug}.jpg` : `${ORIGIN}/share/archive.jpg`);

// JSON-LD only for claims that are visible on the page.
export function useJsonLd(id, data) {
  const json = data ? JSON.stringify(data) : null;
  useEffect(() => {
    if (!json) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.ld = id;
    el.text = json;
    document.head.appendChild(el);
    return () => el.remove();
  }, [id, json]);
}

export const breadcrumbLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.label,
    ...(c.to ? { item: `${ORIGIN}${c.to}` } : {}),
  })),
});

export const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Prism Pixel Creators",
  alternateName: "PrismPixel™",
  url: ORIGIN,
  logo: `${ORIGIN}/share/og-default.jpg`,
  parentOrganization: { "@type": "Organization", name: "Lumen8 Laboratories" },
  email: "charlie@prismpixelcreators.com",
};

export function useSeo(pathname, notFound = false) {
  useEffect(() => {
    const r = notFound ? NOT_FOUND : shareRouteFor(pathname);
    const url = `${ORIGIN}${pathname}`;
    const image =
      r.slug === "home"
        ? `${ORIGIN}/share/og-default.jpg`
        : `${ORIGIN}/share/${r.slug}.jpg`;
    const transactionPage = pathname === "/about/testing/checkout" || pathname.startsWith("/payment/");
    const privatePage = pathname === "/lab/inbox" || pathname === "/lab/publish";
    // Entry pages (archive Nº, makers) set their own title/description after mount.
    const dynamicEntry = r.dynamic && pathname !== r.path;
    if (!dynamicEntry) {
      document.title = transactionPage ? "Secure Checkout — PrismPixel™" : privatePage ? "Laboratory — PrismPixel™" : r.title;
      meta("description", r.description, "name");
      meta("og:title", r.title);
      meta("og:description", r.description);
      meta("og:image", image);
      meta("twitter:title", r.title, "name");
      meta("twitter:description", r.description, "name");
      meta("twitter:image", image, "name");
    }
    meta("og:url", url);
    setTag('link[rel="canonical"]', { tag: "link", rel: "canonical", href: url });
    if (notFound || transactionPage || privatePage) meta("robots", "noindex, nofollow", "name");
    else document.head.querySelector('meta[name="robots"]')?.remove();
  }, [pathname, notFound]);
}
